import { pagesPath } from '~/libs/$path';

// NOTE: ログイン後に元URLへ復帰させるためのクエリキー
export const LOGIN_REDIRECT_QUERY_KEY = 'redirect';

// NOTE: 復帰先として許可しないパス。これらは「未ログイン/初期設定未完了なら、ここへ
// リダイレクトする」側の画面なので、復帰先にすると自己参照ループに陥るため。
// 判定は小文字に正規化して行うため、リスト側も小文字へ揃えておく。
const DENIED_REDIRECT_PATH_PREFIXES = [
  pagesPath.login.$url().path,
  pagesPath.new_password.$url().path,
  pagesPath.signup.$url().path,
].map((path) => path.toLowerCase());

/**
 * ログイン後の遷移先として安全な内部パスかどうかを判定する。
 * オープンリダイレクト対策として、自サイト内の相対パスのみを許可する。
 * - `/` 始まりの相対パスのみ許可
 * - `//` や `/\` で始まる protocol-relative URL は拒否（外部サイトへ誘導されうる）
 * - 空白文字を含むパスは不正として拒否
 * - 復帰先にすると自己参照ループになる画面（`/login` `/new-password` `/signup` 配下）は拒否。
 *   クエリ・ハッシュ・大文字小文字の表記揺れ（例: `/login#x`, `/LOGIN`）ですり抜けないよう
 *   正規化して判定する
 */
export const isSafeInternalRedirectPath = (path: string): boolean => {
  if (!path.startsWith('/')) return false;
  if (path.startsWith('//') || path.startsWith('/\\')) return false;
  if (/\s/.test(path)) return false;

  // クエリ・ハッシュを除いたパス部分を小文字化して認証動線自身か判定する
  const cleanPath = (path.split(/[?#]/)[0] ?? path).toLowerCase();
  if (
    DENIED_REDIRECT_PATH_PREFIXES.some(
      (deniedPath) =>
        cleanPath === deniedPath || cleanPath.startsWith(`${deniedPath}/`),
    )
  ) {
    return false;
  }

  return true;
};

/**
 * 受け取った redirect 値が安全な内部パスであればそれを返し、そうでなければ null を返す。
 */
export const resolveSafeRedirectPath = (
  redirect: string | null | undefined,
): string | null => {
  if (!redirect) return null;
  return isSafeInternalRedirectPath(redirect) ? redirect : null;
};

/**
 * 遷移先パスに復帰先（redirect クエリ）を付与する。安全な内部パスが渡された場合のみ
 * 付与し、そうでなければ渡されたパスをそのまま返す。
 *
 * パスワード変更やアカウント初期設定など、ログイン画面以外の認証動線でも復帰先を
 * 引き継げるよう共通化したもの。
 *
 * NOTE: `path` はクエリを含まない前提（すべての呼び出し元が `pagesPath` の静的パスを渡す）。
 */
export const appendRedirectQuery = (
  path: string,
  returnTo: string | null | undefined,
): string => {
  const safeReturnTo = resolveSafeRedirectPath(returnTo);
  if (!safeReturnTo) return path;

  return `${path}?${LOGIN_REDIRECT_QUERY_KEY}=${encodeURIComponent(safeReturnTo)}`;
};

/**
 * ログイン画面のパスを組み立てる。安全な内部パスが渡された場合のみ、
 * ログイン後の復帰先として redirect クエリを付与する。
 */
export const buildLoginPathWithRedirect = (
  returnTo: string | null | undefined,
): string => appendRedirectQuery(pagesPath.login.$url().path, returnTo);
